
I want to _cautiously_ **voice** 📣 that the following are not strongly held __"opinions"__ but rather attempts to transparently put forth **_extremely hard_** questions rattling around in my head and the ensuing research towards them.

I intend to use this post/page/writing to reach others that might have similar thoughts/questions.

Please, please don't think me evil questioning/considering reality, religiousness, social norms, sexuality, etc. I do not wish to hinder my professional work through this writing and I will do everything in my power to partition these thoughts from my practiced/business work even in places where mathematical philosophy, computer science and software engineering might cross paths.

My contact information is readily available and I embrace discussion! Please feel free to join at https://discord.gg/JemYHj8 for updates, communication, voice chats, etc! Just ping me wherever! I love Discord for its marvelous capabilities of group voice chat.

# background bits

As a sort of "personal mission statement", I want to strive to identify my personal biases or any potential life context that might result in a strong opinion one way or another.

To contextualize my life thus far:

- I am a confirmed Catholic.
  - My father grew up a non-practicing Italian Catholic who found Catholicism through my mother's Irish Catholic family and through raising his own family.
  - My mother grew up a practicing Irish Catholic who was raised within the sacraments (baptism, confirmation, eucharist, matrimony, etc.)
  - Through Catholocism,
    - I have been baptised.
    - I have been confirmed.
    - I have taken the Eucharist.
    - I have confessed my sins through penance.
    - I attended "prayer school religion" once a week through 9th grade.
- I grew up in East Cobb (Marietta/Roswell) Georgia.
- I attended Georgia Institute of Technology.
- I spent 3 separate periods of study and work abroad living in Barcelona, Spain
  - Summer 2014
  - Summer 2015
  - January 2016 - August 2016
- I helped found the company [Glovo](https://glovoapp.com/) with a friend I met Georgia Tech (Oscar Pierre).
  - I joined May 2015.
- I helped found the company [FIXD](https://www.fixdapp.com/) with friends at Georgia Tech (Julian Knight, John Gattuso, Fred Grimm, Eric Pate).
  - I joined January 2017.
- I attended a 10 day Vipassana course at [Dhamma Patāpa](https://www.dhamma.org/en-US/schedules/schpatapa) in August 2015.
- I attempted to start a company from a [friend's thesis paper](https://credcount.com/whitepaper.pdf) by [applying to Y Combinator for the summer 2017 term](https://cdbattaglia.com/y-combinator).
- I currently live in New York city.

Other "statements"?

- I don't presume to hide my biases.
- I heartedly attempt to relinquish my biases.
- I am a privileged white male.
- I am fortunate to have no personal debt.
- I am not currently practicing any single religion.

# personal philosophic identification

A good friend of mine once referred to these "thought chains" as anti-nihilistic:

> Anti-nihilists are courageous because they dare to turn the tables on nihilism. They embrace nihilism. But rather than dwell on it, rather than drown in the glass-is-half-empty ennui of it all, they decide to swim, and they keep swimming.

> It’s our meaninglessness within which to create my own meaning. It’s our illusion to play with. We are the ones laughing at the cosmic joke instead of merely being the butt-end of it.

from https://fractalenlightenment.com/35998/life/authentic-existentialism-from-nihilism-to-anti-nihilism

Some might recoil at this label, but I think it's a wonderful example of breaking free of a formalized system by taking the converse. Anti-nihilism is different than the "opposite of nihilism".

# links that provoked this "article"

- 2019-01-04
  - [JRE MMA Show #32 with Firas Zahabi - YouTube](https://www.youtube.com/watch?v=xDsoWp743gM&app=desktop)
  - ["Thoughts" with Sam Harris (from Joe Rogan Experience #543) - YouTube](https://www.youtube.com/watch?v=hxKR6gtkSYs)
  - [Waking Up With Sam Harris #62 - What is True? (with Jordan B. Peterson) - YouTube](https://www.youtube.com/watch?v=1gdpyzwOOYY)
- 2019-01-07
  - [Joe Rogan has his mind blown by Lawrence Krauss - YouTube](https://www.youtube.com/watch?v=RKapUWxTvWI)
  - [Sam Harris & Jordan Peterson - Vancouver - 1 - YouTube](https://www.youtube.com/watch?v=jey_CzIOfYE)
- 2019-01-11
  - [How to Be an Expert in a Changing World](http://www.paulgraham.com/ecw.html)
  - [You Weren't Meant to Have a Boss](http://www.paulgraham.com/boss.html)
- 2019-02-12
  - [Joe Rogan Experience #1241 - Sam Harris - YouTube](https://www.youtube.com/watch?v=ZA106wrMUe4)

# people

- just above "acquaintance" kind of folks (reachables?):
  - Yuri I. Manin
  - Joe Rogan
  - Firas Zahabi
  - Sam Harris ([Wikipedia](https://en.wikipedia.org/wiki/Sam_Harris))
  - Jordan Peterson
    - changes in human rights code with relevance to his work in Ontario
  - Nick Kokonas
    - owner of Alinea restaurants
    - king of the human experience
  - Lawrence Krauss
-   Tim Ferriss
- _modern_ "entrepreneurs":
  - Elon Musk
  - Peter Thiel (Zero to One)
  - Bill Gates
- _historical_ "academics":
  - Aristotle
  - Newton
  - Descartes
  - Thomas Coon
  - Stephen Hawking
  - Einstein
  - Bertrand Russel
- acquaintances (if you'd like your name taken down then please feel free to contact):
  - Nina Stepanov
  - Mike Zawadski
  - Hersh Patel
  - Anthony O'Sullivan
  - Aaron Harnly
  - Karl Sofinowski
  - Jack Woods
  - Aldo Beqiraj
  - Pooja Thakrar
  - Oscar Pierre
  - Adnane Oa
  - Jesse Vogel

# thoughts

- object permanence
  - baby seeing mother in selfie cam
- physical law is through observation
- heads or tails is through logic or history of coins?
  - coin flip into a butterfly
- is randomness a law of nature
  - randomness is ignorance?
  - the measure of truth is opinion?
  - corrsepondence theory
- Kante
- Pierre-Simon Laplace
- "science is faith that the future will behave like the past"
- "science is the use of measurement to understand what's around us"
- water can resist boiling up til a number past 100 celcius
- show you knives:
  - Aristotle says they share in one essence that makes them knives
  - sword vs knife (objective? subjective?)
  - now matter, matter is an inference of the mind
- e = mc^2
- a train moving at lightspeed in an arc from points B to C around a center point A where distance BC is the same as AB and AC
  - Einstein talking about a light flipping on in the train car that is on the roof of the train car
  - the light is moving slower in the car than it is moving relative to the person standing at point A
- Occam's Razor "believe what you observe"
  - everything with a doubt you chop it
  - everything imaginary you chop
  - trascend all paradigms
    - mystics -> religion is true and science is faith
    - consciousness not the content of consciousness
    - brain is within consciousness
      - no more paradoxes
      - outside of your ego
- materialism
  - epiphenominalism
    - consciousness is like a smoke
    - brains are dependent on consciousness
    - idealism "there is no phyiscal object outside of consciousness"
- when someone dies is the universe still the universe?
- triangle 180 degrees
  - can you picture a triangle without color?
- voxel vs Euclidean geometry vs dimensional theory vs triangle meshes
- is there a better way than friction to stop a car?
- truth vs belief
  - electronic blanket warmth and then later see a flame
  - Descartes = science is doubtable
  - "cartesian doubt"
  - man is more and more precise but science is based on faith that future behaves like the past
  - history is truth?
  - paradigm shift Thomas Coon shades
- teach your children the that what is popularly known as red is actually blue
- reality is a thought experiment?
- cogito
  - "I think; therefore, I am."
    - straw in water then it is bent (eyes lied)
    - hand in cold water it will seem warm (my bias, my instruments are not accurate enough)
    - deduction and math 1+1=2
      - philosophers disagree with math
      - 1+1=2 -> critique 1+1 is 2 there is no other information
      - helps give epiphany
      - f(x) there is a lower pattern (it's always been two things)
        - isomorphism vs tautology
      - Bertrand Russell gives the information again
      - Cante says math tells you something
    - all greatest thinkers in history all disagree
      - Liebnitz and Voltaire
      - Sam Harris and Jordan Peterson
    - we cannot use logic because we can't get to conclusion we can all agree upon
    - doubt everything (what if I'm dreaming?)
  - Imam Razelli
  - Rene Descarte
  - crisis that "it's all explanation and not proof"
  - science gets refined with more information
  - today's paradigm will be changed 100 years from now
  - at end of cartesian doubt "modern philosophy"
  - 6 chapters in 6 days
- immutable documentation
  - this document and the commit hashes for this
  - Slack service for adding data into this privately
  - Twitter for public quotes that retain thread-ability
  - "how-doc"
  - "why-doc"
- dogmatic vs prophetic
- "God is the future to which we make sacrifices"
- ignorance is evil?
  - like not knowing about a disease?
  - there is an incubation period that acts like a "zero"
  - this incubation period is a period of discovery
  - although there is no detection for some up to a while you're in the "zero period"
  - this zero period describes a lack of observable information
  - ignorance is an infection until you accept ignorance
  - that's a zero to a negative one
  - if zero is complacency and 1 is movement then we need ternary for true life detail
  - it's not heaven and hell though
  - it's the z value oscillating over an expanding universe
  - the expansion is the derivation
  - derivative = history
  - integration = future
  - -1 = past
  - 0 is current
  - 1 is future
  - so to go zero to 1 makes sense in this context
- money is immutability as ignorance
  - such ignorance is a bandwidth reliever
  - such bandwidth relievers are like 1+1=2
  - anything that reduces space in the pipe is towards a new contextual zero
- talking to someone and only catching up provokes travel in order to truly connect or talk to someone
  - this inherint path forward is what makes humanity want to internationalize and what breaks down barriers
  - Romeo and Juliet style
  - Rosalia flamenco and electronic
- mice with brainlock and the "Homer Simpson" gene
  - why did humans reach the brainlock gene before all the other animals?
  - evolution is/was a race?
  - harken back to Merlin in the Once and Future king and the need for fiction as a teaching Instrument
  - humans were given one tool and they chose the tool of tools... The first evolutionary derivative
  - choice = happiness?
  - but only when observable choice comes along and serves a basis for some amount of discovery through finding a new flavor?
- holding pole of subway and thinking someone else's hand is yours
  - same feeling as meditation mistaken identity because so external
- the human collective
  - Lambda school has too many engineers
  - how do we have another renaissance of art and thought? When through art we strive for isomorphisms in nature or science
  - time to spare is the opposite or Times Square
- ennui = man's ultimate enemy?
  - definition: a feeling of listlessness and dissatisfaction arising from a lack of occupation or excitement
  - synonyms: boredom, tedium, listlessness, lethargy, lassitude, languor, restlessness, weariness, sluggishness, enervation

# [Cognitive Networks: Brains, Internet, and Civilizations (2017)](https://pdfs.semanticscholar.org/342d/672ba656102fd5a98df2c882723ef3022efe.pdf)

- there is the concept of "conscious sensory perceptions" where ”I see” means ”I know that I see” ... hmmmm
- ok, so it was conjectured that the brain contains inside a map of itself, and that some neural information channels in the central neural system do certain things but pause here...
- working the other day on RSA asymmetric cryptography through open idc and how one way private key signing can be verified through a public key and there's no way through parsing a public key to ever re-sign back to what once was
- so what if our brains operated the same way? we sign our thoughts using our collective wisdom that is a discrete model only ever at delta t and every thought can only ever be parsed using a "public key" that is the collective experience everyone has ever had interpreting me
- ha so then a "soul" can only ever exist with a public key comprised of n > 1 social entities
oh ok, that's why the Bible starts with both an Adam and an Eve, cool
- so how many of y'all ever worked with protobuf or the concept of (de)serialization? let's think of the human brain as a payload of protobuf who's containing `.proto` schema only ever exists inside of the payload itself the same way that AI can mutate it's "rule-set"
- so if our schema is always a function of delta t or hell let's say time doesn't exist in our brains then we're pretty much hosed to ever figure out the brain but we can still implement a protocol based on this infrastructure - that's one hell of a mesh network though...

# Vipassana meditation

- notice what arises in consciousness
- noticing takes so much concentration
- feel identical to the sentence in your head
- how could a thought define subjectivity?
- hearing the sound of voice in appearance of consciousness
- thinking without knowing that your thinking
- playing a video game where not losing in the same boss fight over and over again
- linguistic thoughts are structured as though conversation
  - like playing chess?
- normal state of consciousness chased out of bed by thoughts is not the happy place and is not the alternative
- the goal is not to have a mind without thoughts
  - it's about intentional thought
- what is mindfulness?
- infinitesimally to the smallest degree recognizing your blood boiling from anger or joy
- is mindfulness a superpower?
- sensory deprivation

# Woo-Woo (Gremlins)

- [Maxwell's Demon](https://en.wikipedia.org/wiki/Maxwell%27s_demon)

# [Joe Rogan and Lawrence Krauss](https://www.youtube.com/watch?v=RKapUWxTvWI)

- space is infinite but light is constant
- everything we see emerged from a single point
- what if there were more big bangs
- all big bangs are separate splashes
- is it another universe?
- universe: that region of space that at one time we could have communicated but in the future we could communicate with
- [Gauge theory - Wikipedia](https://en.wikipedia.org/wiki/Gauge_theory)
